'use client';

/**
 * ShellSideDock — the dashboard shell's full-height right rail (BL-1994).
 *
 * The docked lot panel (auction Lot Info, Inventory) belongs to its page's
 * React tree — its state, its unsaved-changes gate — but has to sit OUTSIDE
 * the page's scrolling `<main>`: beside it, from the top of the window to the
 * bottom, edge to edge. Rendered in the page, it could only start below the
 * page header and inside the content padding (the grey band Dan flagged).
 *
 * Same shape as `ContentDock`: `DashboardShell` renders the host as a sibling
 * of the content column, and `SideDockSlot` portals its children into it. A
 * slot with no host (tests, a page outside the shell) renders in place.
 */

import { createContext, useContext, useEffect, useLayoutEffect, useState, type Dispatch, type ReactNode, type RefObject, type SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import { useWindowWidth } from '../../hooks/useWindowWidth';

const HostContext = createContext<HTMLElement | null>(null);
const SetHostContext = createContext<((el: HTMLDivElement | null) => void) | null>(null);
// How many slots are docked right now, and its stable setter — split so a
// count change re-renders only the readers (the SidebarAutoCollapse shape).
const OccupiedCountContext = createContext(0);
const SetOccupiedContext = createContext<Dispatch<SetStateAction<number>> | null>(null);

/**
 * Holds the rail's host element for the shell subtree.
 *
 * @param props.children The shell's columns, one of which renders `ShellSideDockHost`.
 * @returns The provider.
 */
export function ShellSideDockProvider({ children }: { children: ReactNode }) {
  // A callback ref into state, so slots re-render with the host once it exists.
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [occupied, setOccupied] = useState(0);
  return (
    <SetHostContext.Provider value={setHost}>
      <SetOccupiedContext.Provider value={setOccupied}>
        <OccupiedCountContext.Provider value={occupied}>
          <HostContext.Provider value={host}>{children}</HostContext.Provider>
        </OccupiedCountContext.Provider>
      </SetOccupiedContext.Provider>
    </SetHostContext.Provider>
  );
}

/**
 * The rail itself: a full-height column at the shell's right edge. Empty (and
 * so zero-width) until a page docks something into it.
 *
 * @returns The host element.
 */
export function ShellSideDockHost() {
  const setHost = useContext(SetHostContext);
  // BL-2902: not on paper — the printed page is the page column alone.
  return <div ref={setHost} data-testid="shell-side-dock" className="flex h-dvh shrink-0 print:hidden" />;
}

/**
 * Renders `children` in the shell's right rail, or in place when there is none.
 *
 * @param props.children What the page docks (the lot panel).
 * @returns A portal into the rail, or the children inline.
 */
export function SideDockSlot({ children }: { children: ReactNode }) {
  const host = useContext(HostContext);
  const setOccupied = useContext(SetOccupiedContext);
  const docked = host != null;
  useEffect(() => {
    if (!docked || !setOccupied) return;
    setOccupied((n) => n + 1);
    return () => setOccupied((n) => n - 1);
  }, [docked, setOccupied]);
  return host ? createPortal(children, host) : <>{children}</>;
}

/**
 * The content box of an element: its client width (which already excludes a
 * scrollbar or `scrollbar-gutter`) less its horizontal padding.
 *
 * @param el - the element
 * @returns the width in px (≤ 0 when unlaid-out)
 */
function contentBoxWidth(el: HTMLElement): number {
  const style = getComputedStyle(el);
  return el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
}

/**
 * BL-2587: the room a docked panel shares with the page beside it — the panel's
 * own width plus the page scroller's content box (`<main>` less its padding and
 * scrollbar gutter, i.e. what the table actually gets). The sum holds as the
 * panel resizes, so it settles in one pass. Outside the shell (tests, a page
 * with no rail) or before anything is laid out, it is the window's width.
 *
 * @param panelWidthRef - the width the panel currently renders at, in px, kept
 *   current by the caller (read at each measurement, after layout)
 * @returns the room in px (0 only on the server render)
 */
export function useSideDockAvailableWidth(panelWidthRef: RefObject<number>): number {
  const host = useContext(HostContext);
  const windowWidth = useWindowWidth();
  const [room, setRoom] = useState(0);
  useLayoutEffect(() => {
    const page = host?.parentElement?.querySelector<HTMLElement>('[data-page-scroll]');
    if (!page) return undefined;
    const measure = () => {
      const content = contentBoxWidth(page);
      setRoom(content > 0 ? panelWidthRef.current + content : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(page);
    return () => {
      observer.disconnect();
      setRoom(0);
    };
  }, [host, panelWidthRef]);
  return room > 0 ? room : windowWidth;
}

/**
 * Whether a page has docked a panel into the shell's rail right now. The
 * auction header reads it (BL-1994): a docked panel only exists on a window
 * 1024px or wider, so its narrower content column is still a desktop layout —
 * the same reason the docked table keeps its table (BL-1987 QA).
 *
 * @returns true while at least one slot is docked; false outside a shell.
 */
export function useShellSideDockOccupied(): boolean {
  return useContext(OccupiedCountContext) > 0;
}
